import { api, count, esc, failed, fileUrl, formValue, getUser, handle, hasGraphics, isPrinter, isStaff, projectHref, stillPage, qs, relTime, safeNext, setAuth, shortTitle, token, topbar, working } from "./api.js";
import { graphicStatesIn, labelStatesIn, STAGE_CLASS, STAGE_TONE, STAGE_WORD, STAGES } from "../../lib/stage.js";

const main = document.getElementById("main")!;

// A client arriving on their emailed link: trade it for a session.
async function exchangeLink(t: string): Promise<void> {
	document.getElementById("topbar")!.innerHTML = "";
	main.innerHTML = '<div class="login"><p class="mute">Signing you in…</p></div>';
	try {
		const r = await api<{ token: string; user: any }>("/api/members/exchange", { method: "POST", body: { t } });
		setAuth(r.token, r.user);
		location.href = safeNext(qs("next"));
	} catch (ex: any) {
		// Already signed in here (a link opened again past its ten days): carry on to where it led.
		if (token() && getUser()) return void (location.href = safeNext(qs("next")));
		// A dead link asks for a fresh one in one click; the new one lands where this one was headed.
		main.innerHTML = `<div class="login"><h1 class="wordmark">Graphics<span class="dot">.</span></h1><p class="err" style="margin-bottom:1rem">${esc(ex.message)}</p><button class="btn btn-primary" id="relink" type="button">Email me a new link</button><div id="sent" class="hint" style="margin-top:.8rem"></div><p class="hint" style="margin-top:1rem"><a href="/">Sign in another way</a></p></div>`;
		const btn = document.getElementById("relink") as HTMLButtonElement;
		btn.addEventListener("click", async () => {
			btn.disabled = true;
			const sent = document.getElementById("sent")!;
			try {
				const r = await api<{ to: string; dev_link?: string }>("/api/members/relink", { method: "POST", body: { t, next: qs("next") } });
				sent.innerHTML = `Sent to ${esc(r.to)}. It opens the same page and stays good for ten days.${r.dev_link ? ` <a href="${esc(r.dev_link)}">Open it (local only)</a>` : ""}`;
			} catch (e: any) {
				sent.textContent = e.message;
				btn.disabled = false;
			}
		});
	}
}

function renderLogin(): void {
	document.getElementById("topbar")!.innerHTML = "";
	main.innerHTML = `
		<div class="login">
			<h1 class="wordmark">Graphics<span class="dot">.</span></h1>
			<p class="tag">Graphics and labels for Upland exhibits.</p>
			${sessionStorage.getItem("expired") ? '<p class="locked" style="margin-bottom:1rem">Your sign-in ran out. Enter your email below and ask for a new link.</p>' : ""}
			<form id="login">
				<div class="field"><label class="f">Email</label><input type="text" name="username" autocomplete="username" autofocus></div>
				<div class="field"><label class="f">Password <span class="faint">(Upland staff)</span></label><input type="password" name="password" autocomplete="current-password"></div>
				<button class="btn btn-primary" type="submit">Sign in</button>
				<div class="err" id="err"></div>
			</form>
			<div class="or">or</div>
			<button class="btn" id="link" type="button" style="width:100%;justify-content:center">Email me a sign-in link</button>
			<p class="hint" style="margin-top:.6rem">For clients and printers. No password needed.</p>
			<div id="sent" class="hint"></div>
		</div>`;
	sessionStorage.removeItem("expired");
	document.getElementById("link")!.addEventListener("click", async () => {
		const email = formValue(main, "username").trim();
		const sent = document.getElementById("sent")!;
		if (!email.includes("@")) {
			sent.textContent = "Enter your email address above first.";
			return;
		}
		try {
			const r = await api<{ dev_link?: string }>("/api/members/link", { method: "POST", body: { email } });
			sent.innerHTML = `If ${esc(email)} is on a project or at a printer we work with, a link is on its way. It stays good for ten days.${r.dev_link ? ` <a href="${esc(r.dev_link)}">Open it (local only)</a>` : ""}`;
		} catch (ex: any) {
			sent.textContent = ex.message;
		}
	});
	document.getElementById("login")!.addEventListener("submit", async (e) => {
		e.preventDefault();
		const f = e.target as HTMLFormElement;
		const errEl = document.getElementById("err")!;
		errEl.textContent = "";
		try {
			const r = await api<{ token: string; user: any }>("/api/login", { method: "POST", body: { username: formValue(f, "username"), password: formValue(f, "password") } });
			setAuth(r.token, r.user);
			location.href = safeNext(qs("next"));
		} catch (ex: any) {
			errEl.textContent = ex.message;
		}
	});
}

async function renderProjects(): Promise<void> {
	topbar({});
	main.classList.add("narrow");
	main.innerHTML = `
		<div class="head">
			<h1>Projects</h1>
			<div class="actions">${isStaff() ? '<button class="btn btn-primary" id="new">New project</button>' : ""}</div>
		</div>
		<form id="newform" class="card hidden">
			<div class="grid3">
				<div class="field"><label class="f">Project</label><input type="text" name="name" placeholder="Pavelka Farmstead"><p class="hint" style="margin-top:.25rem">The job only; the client and short name go beside it.</p></div>
				<div class="field"><label class="f">Short name</label><input type="text" name="code" placeholder="PAVELKA" maxlength="16" style="text-transform:uppercase"><p class="hint" style="margin-top:.25rem">ODIN's short name for the project, up to 16 letters; type a few letters to find it. Goes on POs, batch names and the printer's files.</p></div>
				<div class="field"><label class="f">Client</label><input type="text" name="client" placeholder="National WWI Museum and Memorial"></div>
			</div>
			<div class="found" id="found" hidden></div>
			<div class="row" style="margin-top:.9rem"><button class="btn btn-primary" type="submit">Create project</button><button class="btn btn-ghost" type="button" id="cancel">Cancel</button><span class="err" id="newerr"></span></div>
		</form>
		<div class="card" id="list"><p class="loading">${working("Loading…")}</p></div>`;
	const form = document.getElementById("newform") as HTMLFormElement;
	document.getElementById("new")?.addEventListener("click", () => {
		form.classList.remove("hidden");
		(form.querySelector("input") as HTMLInputElement).focus();
	});
	document.getElementById("cancel")?.addEventListener("click", () => form.classList.add("hidden"));
	// ODIN knows the projects: a few letters in Project or Short name offer its matches, one click fills both.
	const found = document.getElementById("found")!;
	let lookup: ReturnType<typeof setTimeout> | undefined;
	const offer = async () => {
		const q = (formValue(form, "code") || formValue(form, "name")).trim();
		if (q.length < 2) return void (found.hidden = true);
		const rows = await api<{ name: string; code: string }[]>(`/api/odin/projects?q=${encodeURIComponent(q)}`).catch(() => []);
		if (!rows.length) return void (found.hidden = true);
		found.hidden = false;
		found.innerHTML = `<span class="faint">In ODIN:</span> ${rows.map((r) => `<button type="button" class="chip" data-code="${esc(r.code)}" data-name="${esc(r.name)}">${r.code ? `<b>${esc(r.code)}</b> ` : ""}${esc(r.name)}</button>`).join("")}`;
		found.querySelectorAll<HTMLButtonElement>("[data-code]").forEach((b) =>
			b.addEventListener("click", () => {
				(form.querySelector('input[name="code"]') as HTMLInputElement).value = b.dataset.code || "";
				if (!formValue(form, "name")) (form.querySelector('input[name="name"]') as HTMLInputElement).value = b.dataset.name || "";
				found.hidden = true;
			}),
		);
	};
	for (const n of ["name", "code"]) form.querySelector<HTMLInputElement>(`input[name="${n}"]`)?.addEventListener("input", () => {
		clearTimeout(lookup);
		lookup = setTimeout(offer, 250);
	});
	form.addEventListener("submit", async (e) => {
		e.preventDefault();
		try {
			const p = await api<{ id: string }>("/api/projects", { method: "POST", body: { name: formValue(form, "name"), code: formValue(form, "code"), client: formValue(form, "client") } });
			location.href = projectHref(p.id);
		} catch (ex: any) {
			document.getElementById("newerr")!.textContent = ex.message;
		}
	});

	const staff = isStaff();
	// The archive is asked for alongside, and folds in when it arrives: the list never waits on it.
	const archivedP = staff ? api<any[]>("/api/projects?archived=1").catch(() => [] as any[]) : Promise.resolve([] as any[]);
	const projects = await api<any[]>("/api/projects");
	const list = document.getElementById("list")!;
	if (!projects.length) {
		list.innerHTML = staff ? stillPage({ title: "No projects yet", text: "Start one for each exhibit." }) : stillPage({ title: "You're not on a project yet", text: "Ask your Upland designer to add you; a sign-in link follows by email." });
	} else {
		// A card per project (Joel, 2026-10-29: as plain as an order page):
		// the band says what and where it stands, the body shows the work and
		// the one move that is Upland's. A find box for when the list grows.
		list.classList.remove("card");
		const find = projects.length > 4 ? `<label class="pfind"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg><input type="search" id="find" placeholder="Find a project or order" aria-label="Find a project or order" autocomplete="off"></label>` : "";
		// Exhibits and product orders are worked apart, so they sit apart
		// (Joel, 2026-10-30: two columns, both always in view). Exhibits keep
		// the card; an order is a row: its number, product, customer, where it
		// stands. One kind alone takes the whole width.
		const exhibits = projects.filter((p) => !p.order);
		const orders = projects.filter((p) => p.order);
		const col = (title: string, n: number, body: string) => `<section class="pcol"><h2 class="psec">${title} <span class="faint">${n}</span></h2>${body}</section>`;
		const left = exhibits.length ? col("Exhibits", exhibits.length, `<div class="pcards">${exhibits.map(card).join("")}</div>`) : "";
		const right = orders.length ? col("Product orders", orders.length, `<div class="orders">${orders.map(orderRow).join("")}</div>`) : "";
		main.classList.toggle("narrow", !(left && right)); // two columns take the page's full width
		list.innerHTML = `${find}<div class="ptwo${left && right ? "" : " one"}">${left}${right}</div>`;
		list.querySelectorAll<HTMLElement>("[data-href]").forEach((c) =>
			c.addEventListener("click", (e) => {
				if (!(e.target as HTMLElement).closest("a")) location.href = c.dataset.href!;
			}),
		);
		document.getElementById("find")?.addEventListener("input", (e) => {
			const q = (e.target as HTMLInputElement).value.trim().toLowerCase();
			list.querySelectorAll<HTMLElement>("[data-find]").forEach((c) => (c.hidden = !!q && !(c.dataset.find || "").includes(q)));
		});
	}
	void archivedP.then((archived) => archived.length && archive(archived));
}

// One project's card.
function card(p: any): string {
	const href = projectHref(p.id, hasGraphics(p));
	const s = standing(p);
	const client = esc(p.client || "");
	const pics = p.pictures?.ids?.length
		? `<div class="pics">${p.pictures.ids.map((id: string) => `<img src="${picUrl(p, id)}" alt="" loading="lazy">`).join("")}${p.pictures.of > p.pictures.ids.length ? `<span class="more">+${p.pictures.of - p.pictures.ids.length}</span>` : ""}</div>`
		: "";
	const counts = s.chips.length ? `<p class="counts">${s.chips.map((c) => `<span class="${c.tone}">${c.n} ${esc(c.word)}</span>`).join('<span class="em">·</span>')}</p>` : "";
	const move = s.move ? `<a class="btn ${s.move.live ? "btn-live" : ""} move" href="${s.move.href}">${esc(s.move.text)}</a>` : "";
	return `<div class="pcard" data-href="${href}" data-find="${esc(`${p.name} ${p.client || ""} ${p.code || ""}`.toLowerCase())}">
		<div class="band"><span class="name"><a href="${href}">${esc(shortTitle(p))}</a></span><span class="status state state-${s.cls}">${esc(s.word)}</span>${client || s.when ? `<span class="client">${client}</span><span class="when">${esc(relTime(p.updated_at))}</span>` : ""}</div>
		<div class="pbody">${pics}${counts}${move}</div>
	</div>`;
}


// A picture on a card: the drawn proof, or the artwork until the print file is made.
const picUrl = (p: any, id: string) => fileUrl(p.pictures?.art?.includes(id) ? `/api/versions/${id}/art-preview` : `/api/versions/${id}/preview`);

// One product order's row: the web order number, the product, the customer,
// how many graphics, where it stands, and its newest picture.
function orderRow(p: any): string {
	const href = projectHref(p.id, hasGraphics(p));
	const s = standing(p);
	const g = Object.values(p.graphics as Record<string, number>).reduce((a, b) => a + b, 0);
	const pic = p.pictures?.ids?.length ? `<img class="thumb" src="${picUrl(p, p.pictures.ids[0])}" alt="" loading="lazy">` : "";
	return `<div class="orow" data-href="${href}" data-find="${esc(`${p.name} ${p.client || ""} ${p.code || ""}`.toLowerCase())}">
		<span class="num">${esc(handle(p))}</span>
		<span class="what"><a href="${href}">${esc(p.client || shortTitle(p))}</a>${p.client ? `<span class="product">${esc(shortTitle(p))}</span>` : ""}</span>
		<span class="right">${g > 1 ? `<span class="n">${g}</span>` : ""}<span class="status state state-${s.cls}">${esc(s.word)}</span>${pic}</span>
	</div>`;
}

// Archived projects, folded under the list: bring one back, or delete it for
// good (typed name, server checks again).
function archive(rows: any[]): void {
	const box = document.createElement("div");
	box.className = "archive";
	main.appendChild(box);
	let open = false;
	const draw = () => {
		box.innerHTML = `<button class="linkish" id="archtoggle" type="button" aria-expanded="${open}">Archived (${rows.length})</button>${
			open
				? `<div class="card"><table class="list"><tbody>${rows
						.map(
							(p) =>
								`<tr><td class="pname"><span>${esc(p.name)}</span>${p.client ? `<span class="client">${esc(p.client)}</span>` : ""}</td><td class="mute small">Archived ${esc(relTime(p.archived_at))}</td><td class="num"><button class="btn btn-sm" data-back="${p.id}">Bring back</button> <button class="btn btn-ghost btn-sm" data-delete="${p.id}">Delete…</button></td></tr>`,
						)
						.join("")}</tbody></table></div>`
				: ""
		}`;
		box.querySelector("#archtoggle")!.addEventListener("click", () => {
			open = !open;
			draw();
		});
		box.querySelectorAll<HTMLButtonElement>("[data-back]").forEach((b) =>
			b.addEventListener("click", async () => {
				b.disabled = true;
				await api(`/api/projects/${b.dataset.back}`, { method: "PUT", body: { archived: false } });
				location.reload();
			}),
		);
		box.querySelectorAll<HTMLButtonElement>("[data-delete]").forEach((b) => b.addEventListener("click", () => confirmDelete(rows.find((p) => p.id === b.dataset.delete))));
	};
	draw();
}

function confirmDelete(p: any): void {
	const dlg = document.createElement("dialog");
	dlg.innerHTML = `<h3>Delete ${esc(p.name)}?</h3>
		<form method="dialog">
			<p>Its graphics, labels, proofs, comments, approvals, batches and stored files are deleted for good. There is no undo.</p>
			<div class="field"><label class="f" for="del-name">Type the project's name</label><input type="text" id="del-name" autocomplete="off"></div>
			<div class="foot"><button class="btn btn-danger" type="submit" disabled>Delete for good</button><button class="btn btn-ghost" type="button">Cancel</button><span class="err"></span></div>
		</form>`;
	document.body.appendChild(dlg);
	const input = dlg.querySelector("input")!;
	const go = dlg.querySelector<HTMLButtonElement>("[type=submit]")!;
	input.addEventListener("input", () => (go.disabled = input.value.trim() !== p.name.trim()));
	dlg.querySelector(".btn-ghost")!.addEventListener("click", () => dlg.close());
	dlg.addEventListener("close", () => dlg.remove());
	dlg.querySelector("form")!.addEventListener("submit", async (e) => {
		e.preventDefault();
		go.disabled = true;
		try {
			await api(`/api/projects/${p.id}`, { method: "DELETE", body: { name: input.value } });
			location.reload();
		} catch (ex: any) {
			(dlg.querySelector(".err") as HTMLElement).textContent = ex.message;
		}
	});
	dlg.showModal();
	input.focus();
}

// Labels and graphics pooled onto the ladder (lib/stage.ts), furthest
// behind first. A project stands where its furthest-behind graphic or
// label does; the other stages follow as chips.
const LADDER = STAGES.map((key) => ({ key, cls: STAGE_CLASS[key], word: STAGE_WORD[key], tone: STAGE_TONE[key], label: labelStatesIn(key), graphic: graphicStatesIn(key) }));
type Move = { text: string; href: string; live: boolean };
function standing(p: { id: string; labels: Record<string, number>; graphics: Record<string, number>; printing?: { id: string; name: string; word: string; turn: string; stopped: boolean }[]; updated_at?: string }): { word: string; cls: string; when: string; chips: { n: number; word: string; tone: string }[]; move: Move | null } {
	const sum = (from: Record<string, number>, keys: string[]) => keys.reduce((n, k) => n + (from[k] || 0), 0);
	const stages = LADDER.map((s) => ({ ...s, n: sum(p.labels, s.label) + sum(p.graphics, s.graphic) }));
	const total = stages.reduce((n, s) => n + s.n, 0);
	const g = Object.values(p.graphics).reduce((a, b) => a + b, 0);
	const l = Object.values(p.labels).reduce((a, b) => a + b, 0);
	const n = (key: string) => stages.find((s) => s.key === key)!.n;
	// Upland's one move, in order of what's waiting on them: prints back,
	// approval to ask for, revisions, a batch to make. The client reads
	// whether anything waits on them.
	const stopped = (p.printing || []).find((j) => j.stopped);
	const back = (p.printing || []).find((j) => j.turn === "upland");
	let move: Move | null = null;
	if (isStaff()) {
		if (stopped) move = { text: `Answer the printer: ${stopped.word.toLowerCase()}`, href: `/batch?id=${stopped.id}`, live: true };
		else if (back) move = { text: "Check in the prints that are back", href: `/batch?id=${back.id}`, live: true };
		else if (n("cleared")) move = { text: `Ask print approval for ${count(n("cleared"), "graphic")}`, href: `/graphics?id=${p.id}`, live: true };
		else if (n("requested")) move = { text: `Revise ${count(n("requested"), "graphic")}`, href: `/graphics?id=${p.id}`, live: true };
		else if (n("approved")) move = { text: `Make a batch: ${n("approved")} approved`, href: `/graphics?id=${p.id}`, live: true };
	}
	if (!total) return { word: "Not started", cls: "draft", when: p.updated_at || "", chips: [], move: null };
	const ready = stages.find((s) => s.key === "review")!;
	const at = !isStaff() && ready.n ? { ...ready, word: "Needs your review" } : stages.find((s) => s.n)!;
	const chips = stages.filter((s) => s.n && s.key !== at.key).map((s) => ({ n: s.n, word: s.word.toLowerCase(), tone: s.tone }));
	// The whole: how many graphics and labels there are.
	// The other stages are said only when they're what's waiting on someone
	// or on Upland; the rest is the whole: how many graphics and labels.
	const said = chips.filter((c) => c.tone);
	if (g) said.push({ n: g, word: g === 1 ? "graphic" : "graphics", tone: "" });
	if (l) said.push({ n: l, word: l === 1 ? "label" : "labels", tone: "" });
	return { word: at.word, cls: at.cls, when: p.updated_at || "", chips: said, move };
}

if (qs("t")) exchangeLink(qs("t"));
else if (!token() || !getUser()) renderLogin();
// A printer's person has no projects: their home is the batches sent to them.
else if (isPrinter()) location.replace("/jobs");
else renderProjects().catch((e) => failed(main, e, renderProjects));
